// web/src/theme/applyTheme/_tokens.ts —— 内部：各档位 token 组（线框 / 圆角 / 宽度 / 密度 / 主题 → html 与 body）
// 拆自 web/src/theme/applyTheme.ts 的同名分区（内容原样搬运）；由 web/src/theme/applyTheme.ts 以 export * 聚合，导出面对调用方不变。

import { CHART_COLORS, CHART_PALETTE } from '../../utils/chartTheme';
import { syncPaletteColors } from '../palette';
import { BORDER_LEVELS, DEFAULT_BORDER_LEVEL, getBorderLevel, type BorderLevelDef, type BorderToneSpec } from '../borders';
import {
  DEFAULT_DENSITY,
  DEFAULT_LANDING,
  DEFAULT_MENU_EXPAND,
  DEFAULT_MOTION,
  DEFAULT_RADIUS,
  DEFAULT_TIME_FMT,
  DEFAULT_WIDTH,
  MOTION_MEDIA_QUERY,
  getDensityLevel,
  getLandingLevel,
  getMenuExpandLevel,
  getMotionLevel,
  getRadiusLevel,
  getTimeFmtLevel,
  getWidthLevel,
  resolveRadius,
  type DensityLevelDef,
  type RadiusLevelDef,
  type WidthLevelDef,
} from '../uiPrefs';
import { DEFAULT_THEME_KEY, getTheme, type ThemeDef } from '../themes';

import { alpha, complementHex, mix, rgbTriplet, toneOf } from './_color';

/** 线框档位相关 token（写 html 的那部分：装饰描边）——切档位时只需重写这一组 */
export function borderRootVars(t: ThemeDef, b: BorderLevelDef): Record<string, string> {
  return {
    '--bcc-border': alpha(t.primary, b.border),
    '--bcc-border-strong': alpha(t.primary, b.borderStrong),
  };
}

/** 线框档位相关 token（写 body 的那部分：Arco 结构描边 + 控件描边 + 预览色） */
export function borderBodyVars(t: ThemeDef, b: BorderLevelDef): Record<string, string> {
  return {
    '--color-border-2': alpha(t.primary, b.colorBorder2),
    '--color-border-3': alpha(t.primary, b.colorBorder3),
    '--bcc-border-control': toneOf(t, b.control),
    '--bcc-border-control-hover': toneOf(t, b.controlHover),
    /* 三档的控件描边与卡片描边同时下发，供设置抽屉"三档并排对比"预览使用（同一套派生逻辑，不写死色值） */
    ...Object.fromEntries(
      BORDER_LEVELS.flatMap((lv) => [
        [`--bcc-border-preview-${lv.key}`, toneOf(t, lv.control)],
        [`--bcc-border-preview-${lv.key}-card`, alpha(t.primary, lv.border)],
      ]),
    ),
  };
}

/** 圆角组 token（写 html）：卡片 / 按钮输入 / 胶囊 / 选择器档；四值由 uiPrefs.resolveRadius 解析 */
export function radiusRootVars(t: ThemeDef, r: RadiusLevelDef): Record<string, string> {
  const v = resolveRadius(r, t.radiusCard);
  return {
    '--bcc-radius-card': `${v.card}px`,
    '--bcc-radius-btn': `${v.control}px`,
    '--bcc-radius-input': `${v.control}px`,
    '--bcc-radius-pill': `${v.pill}px`,
    '--bcc-radius-sm': `${v.small}px`,
  };
}

/** 圆角组 token（写 body）：Arco 运行时圆角（按钮/输入/菜单/弹层） */
export function radiusBodyVars(t: ThemeDef, r: RadiusLevelDef): Record<string, string> {
  const v = resolveRadius(r, t.radiusCard);
  return {
    '--border-radius-small': `${v.control}px`,
    '--border-radius-medium': `${v.control}px`,
  };
}

/** 宽度组 token（写 html）：内容区最大宽度（`none` = 通栏） */
export function widthRootVars(w: WidthLevelDef): Record<string, string> {
  return { '--bcc-content-max': w.max };
}

/** 密度组 token（写 html）：表格单元格纵向内边距；`standard` 档不写值（= tokens.css 现值） */
export function densityRootVars(d: DensityLevelDef): Record<string, string> {
  return { '--bcc-table-cell-y': `${d.cellY}px` };
}

/**
 * 动效档位 → 实际是否减弱：`system` 档按操作系统的"减少动态效果"设置解析。
 * 解析结果写到 `body[data-bcc-motion]`（CSS 只认 standard / reduced 两个值），
 * 存储里保留的是用户选的三档之一，故"跟随系统"在系统设置变化时能自动跟随。
 */
export function resolveMotion(key: string): string {
  if (key !== 'system') return key;
  try {
    return window.matchMedia(MOTION_MEDIA_QUERY).matches ? 'reduced' : 'standard';
  } catch {
    return 'standard';
  }
}

/** 主题组 token（写 html）：主色/AI/文字/渐变/表头/胶囊色——不含描边、圆角、宽度（各归各自的组） */
export function themeRootVars(t: ThemeDef): Record<string, string> {
  return {
    '--bcc-primary': t.primary,
    '--bcc-primary-rgb': rgbTriplet(t.primary),
    '--bcc-primary-deep': t.primary2,
    '--bcc-primary-bg': alpha(t.primary, 0.08),
    '--bcc-primary-bg-hover': alpha(t.primary, 0.06),
    /* 主色透明档：供 arco-theme.less 与全局样式复用（Less 无法对运行时变量加 alpha） */
    '--bcc-primary-a04': alpha(t.primary, 0.04),
    '--bcc-primary-a05': alpha(t.primary, 0.05),
    '--bcc-primary-a12': alpha(t.primary, 0.12),
    /* 主色对比色：荧光笔等「必须与主色拉开」的强调底色，随主题切换（主色色相 +180°） */
    '--bcc-highlight-rgb': rgbTriplet(complementHex(t.primary)),
    '--bcc-ai': t.ai,
    '--bcc-ai-rgb': rgbTriplet(t.ai),
    '--bcc-ai-bg': alpha(t.ai, 0.1),
    '--bcc-ai-light': t.aiLight,
    /* AI 文字档：AI 色作文字在白底上普遍 <3:1，小字场景改用加深档（浅底 ≥4.5:1） */
    '--bcc-ai-deep': t.aiDeep,
    '--bcc-bg': t.bg,
    '--bcc-text-1': t.text1,
    '--bcc-text-2': t.text2,
    '--bcc-text-3': t.text3,
    '--bcc-grad-primary': `linear-gradient(135deg, ${t.primary} 0%, ${t.gradTo} 100%)`,
    '--bcc-grad-ai': `linear-gradient(135deg, ${t.primary} 0%, ${t.ai} 100%)`,
    '--bcc-content-bg': `radial-gradient(1000px 420px at 18% -8%, ${alpha(t.ai, t.glowAlpha)}, transparent 58%), linear-gradient(180deg, ${t.bgTop} 0%, ${mix(t.primary, '#ffffff', 0.04)} 360px, ${t.bg} 100%)`,
    '--bcc-sider-bg': `linear-gradient(180deg, ${t.siderFrom} 0%, ${t.siderTo} 100%)`,
    '--bcc-shadow-hover': t.shadowHover,
    '--bcc-glow-accent': t.glow,
    '--bcc-focus-ring': `0 0 0 3px ${alpha(t.primary, 0.12)}`,
    '--bcc-th-bg': alpha(t.primary, 0.08),
    /* 固定列必须用不透明等效色：半透明底在横向滚动时会叠穿下层列 */
    '--bcc-th-bg-solid': mix(t.primary, '#ffffff', 0.08),
    '--bcc-row-hover-solid': mix(t.primary, '#ffffff', 0.05),
    '--pill-arcoblue': t.primary,
    '--pill-arcoblue-fg': t.primary2,
    '--pill-arcoblue-bg': alpha(t.primary, 0.1),
    '--pill-arcoblue-border': alpha(t.primary, 0.25),
    '--pill-blue': t.primary,
    '--pill-blue-fg': t.primary2,
    '--pill-blue-bg': alpha(t.primary, 0.1),
    '--pill-blue-border': alpha(t.primary, 0.25),
    '--pill-cyan': t.ai,
    '--pill-cyan-fg': t.aiDeep,
    '--pill-cyan-bg': alpha(t.ai, 0.12),
    '--pill-cyan-border': alpha(t.ai, 0.25),
  };
}

/** 主题组 token（写 body）：Arco 弧蓝 + 深色壳——不含虚线框组与圆角组 */
export function themeBodyVars(t: ThemeDef): Record<string, string> {
  const vars: Record<string, string> = {
    '--arcoblue-1': rgbTriplet(t.ramp[0]),
    '--arcoblue-2': rgbTriplet(t.ramp[1]),
    '--arcoblue-3': rgbTriplet(t.ramp[2]),
    '--arcoblue-4': rgbTriplet(t.ramp[3]),
    '--arcoblue-5': rgbTriplet(t.ramp[4]),
    '--arcoblue-6': rgbTriplet(t.primary),
    '--arcoblue-7': rgbTriplet(t.primary2),
    '--arcoblue-8': rgbTriplet(t.ramp[7]),
    '--arcoblue-9': rgbTriplet(t.ramp[8]),
    '--arcoblue-10': rgbTriplet(t.ramp[9]),
  };
  // 深色壳：只有壳层文字/描边/菜单底翻转，内容区与顶栏不受影响
  if (t.shell) {
    vars['--bcc-shell-text-2'] = t.shell.text2;
    vars['--bcc-shell-text-3'] = t.shell.text3;
    vars['--bcc-shell-border'] = t.shell.border;
    vars['--bcc-shell-menu-hover-bg'] = t.shell.menuHoverBg;
    vars['--bcc-shell-menu-selected-bg'] = t.shell.menuSelectedBg;
    vars['--bcc-shell-menu-hover-color'] = t.shell.menuHoverColor;
    vars['--bcc-shell-menu-selected-color'] = t.shell.menuSelectedColor;
  }
  return vars;
}

